import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, MoreVertical, Smartphone, Monitor } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

let deferred = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; listeners.forEach((l) => l()); });
  window.addEventListener("appinstalled", () => { deferred = null; listeners.forEach((l) => l()); });
}

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-center gap-4 rounded-2xl bg-white/[0.04] p-4">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#25e46b] font-display text-lg font-black text-[#0b132b]">{n}</span>
    <span className="flex-1 text-sm text-slate-200">{children}</span>
    <Icon size={20} className="shrink-0 text-[#25e46b]" />
  </li>
);

const HelpDialog = ({ open, onOpenChange }) => {
  const ios = isIOS();
  const desktop = !ios && !isMobile();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#111c38] sm:max-w-md" data-testid="install-help-dialog">
        <DialogHeader>
          <p className="eyebrow">Application FOOTZA</p>
          <DialogTitle className="font-display text-3xl font-extrabold uppercase">Installer sur ton {ios ? "iPhone" : "appareil"}</DialogTitle>
          <DialogDescription className="text-slate-400">Gratuit, sans store, en 10 secondes. FOOTZA s'ouvrira comme une vraie app, en plein écran.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3">
          {ios ? (
            <>
              <Step n={1} icon={Share}>Ouvre FOOTZA dans <b>Safari</b> et touche le bouton <b>Partager</b> en bas de l'écran</Step>
              <Step n={2} icon={PlusSquare}>Choisis <b>« Sur l'écran d'accueil »</b></Step>
              <Step n={3} icon={Smartphone}>Touche <b>Ajouter</b> : l'icône FOOTZA apparaît sur ton écran</Step>
            </>
          ) : desktop ? (
            <>
              <Step n={1} icon={Monitor}>Ouvre FOOTZA dans <b>Chrome</b> ou <b>Edge</b> sur ton ordinateur</Step>
              <Step n={2} icon={Download}>Clique sur l'icône <b>Installer</b> à droite de la barre d'adresse</Step>
              <Step n={3} icon={MoreVertical}>Ou menu <b>⋮</b> → <b>« Caster, enregistrer et partager »</b> → <b>« Installer la page »</b></Step>
            </>
          ) : (
            <>
              <Step n={1} icon={MoreVertical}>Ouvre FOOTZA dans <b>Chrome</b> ou <b>Edge</b> et touche le menu <b>⋮</b></Step>
              <Step n={2} icon={Download}>Choisis <b>« Installer l'application »</b> ou <b>« Ajouter à l'écran d'accueil »</b></Step>
              <Step n={3} icon={Smartphone}>Confirme : l'icône FOOTZA apparaît sur ton écran</Step>
            </>
          )}
        </ol>
        {ios && <p className="text-xs text-slate-500">Sur iPhone, active ensuite les notifications depuis ton profil dans l'app installée pour recevoir les rappels et les alertes de buts.</p>}
      </DialogContent>
    </Dialog>
  );
};

export const InstallButton = ({ className = "fz-btn-ghost w-full", label = "Installer l'app", testid = "pwa-install-button" }) => {
  const [, force] = useState(0);
  const [help, setHelp] = useState(false);
  useEffect(() => { const l = () => force((x) => x + 1); listeners.add(l); return () => listeners.delete(l); }, []);
  if (isStandalone()) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      deferred = null;
      force((x) => x + 1);
    } else setHelp(true);
  };

  return (
    <>
      <button data-testid={testid} className={className} onClick={install}><Download size={16} /> {label}</button>
      <HelpDialog open={help} onOpenChange={setHelp} />
    </>
  );
};

export const InstallBanner = () => {
  const [hidden, setHidden] = useState(() => localStorage.getItem("fz_install_banner") === "off");
  if (hidden || isStandalone()) return null;
  return (
    <div className="fz-card flex items-center gap-4 border-[#25e46b]/25 p-4 lg:hidden" data-testid="install-banner">
      <img src="/icon-192.png" alt="" className="h-12 w-12 rounded-xl" />
      <div className="min-w-0 flex-1"><p className="text-sm font-extrabold">Installe l'app FOOTZA</p><p className="text-xs text-slate-400">Rapide, plein écran.</p></div>
      <InstallButton className="fz-btn-primary h-9 px-4 text-xs" label="Installer" testid="install-banner-button" />
      <button onClick={() => { localStorage.setItem("fz_install_banner", "off"); setHidden(true); }} className="text-lg leading-none text-slate-500" data-testid="install-banner-close" aria-label="Fermer">×</button>
    </div>
  );
};
